import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, Loader2, ArrowLeft, ChevronDown, Zap, Car, TrainFront, Facebook, Copy, MessageCircle, Send, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Calendar } from "@/components/ui/calendar";
import { format, getDay } from "date-fns";
import { cn } from "@/lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ImageGallery from "@/components/modules/ImageGallery";
import DynamicTimeSlots from "@/components/listing_booking_page/DynamicTimeSlots";
import PaymentMethodSelector from "@/components/listing_booking_page/PaymentMethodSelector";
import RecurringBookingPanel from "@/components/listing_booking_page/RecurringBookingPanel";
import type { PlannedOccurrence } from "@/lib/recurringSchedule";
import { useDynamicPricing } from "@/hooks/useDynamicPricing";
import { useBusinessPaymentConfig } from "@/hooks/useBusinessPaymentConfig";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { isStripeTestMode } from "@/config/stripePlans";
import { APPOINTMENT_BOOKING_ENABLED } from "@/config/featureFlags";
import { useDynamicFavicon } from "@/hooks/useDynamicFavicon";
import * as Sentry from "@sentry/react";
import { ErrorFallback } from "@/components/ErrorFallback";
import {
  bookingAccessUrl,
  createPublicBooking,
  isBookingRuleError,
  isPastBookingCutoff,
  readBookingError,
  getPublicSlotUsage,
  upsertPublicCustomer,
} from "@/lib/publicBooking";

interface ScheduleData {
  type?: 'recurring' | 'one_off';
  weekly: Record<string, string[]>;
  exceptions: { date: string; slots: string[]; isClosed: boolean }[];
}

interface Listing {
  listing_id: string;
  name: string;
  description?: string;
  teacher_name?: string | null;
  duration: string;
  spots: number;
  price: number;
  time: string;
  outlet_id?: string;
  booking_mode?: string | null;
  duration_minutes?: number | null;
  buffer_minutes?: number | null;
  images?: string[];
  terms_and_conditions?: string | null;
  tags?: string[];
  schedule?: ScheduleData | null;
  default_billing_mode?: string | null;
  faqs?: { question: string; answer: string }[];
  custom_questions?: { id: string; question: string; type: "text" | "dropdown" | "radio"; required: boolean; options?: string[] }[];
  waitlist_enabled?: boolean;
  collect_attendee?: boolean;
  booking_cutoff_hours?: number | null;
}

const TAG_INFO: Record<string, { label: string; icon: React.ElementType }> = {
  instant_confirmation: { label: "Instant confirmation", icon: Zap },
  parking_available: { label: "Parking available", icon: Car },
  near_public_transport: { label: "Near public transport", icon: TrainFront },
};

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const formatTimeTo12h = (time24: string): string => {
  const [hStr, mStr] = time24.split(":");
  let h = parseInt(hStr) || 0;
  const m = mStr || "00";
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${period}`;
};

const parseTime24ToMinutes = (timeStr: string): number => {
  const [hStr, mStr] = timeStr.split(":");
  return (parseInt(hStr) || 0) * 60 + (parseInt(mStr) || 0);
};

const ListingBooking = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listing, setListing] = useState<Listing | null>(null);
  const [businessSlug, setBusinessSlug] = useState<string | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [businessAvatarUrl, setBusinessAvatarUrl] = useState<string | null>(null);
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string | undefined>(undefined);
  const [pax, setPax] = useState<number>(1);
  const [paymentMethod, setPaymentMethod] = useState<"stripe" | "manual">("manual");
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    notes: "",
  });
  const [customFieldResponses, setCustomFieldResponses] = useState<Record<string, any>>({});
  // Phase C2. Attendee (child) capture, shown only when listing.collect_attendee.
  const [attendeeName, setAttendeeName] = useState("");
  const [attendeeLevel, setAttendeeLevel] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [recurringPlan, setRecurringPlan] = useState<PlannedOccurrence[]>([]);
  const [waitlistJoining, setWaitlistJoining] = useState(false);
  const [waitlistJoined, setWaitlistJoined] = useState(false);

  // Appointment mode (booking_mode='appointment')
  // Availability comes from the get-appointment-availability edge fn (the same
  // engine the AI uses), not the weekly grid. Staff = resources; "any" assigns
  // the earliest-free; a chosen staff filters to them.
  // Gated by the class-based vertical flag: when appointment booking is OFF, a
  // listing authored in appointment mode is NOT treated as appointment (no staff
  // picker), and is surfaced as "not bookable online" below rather than falling
  // through to a session grid built from a stale schedule.
  const isAppointment = APPOINTMENT_BOOKING_ENABLED && listing?.booking_mode === "appointment";
  const appointmentDisabled = !APPOINTMENT_BOOKING_ENABLED && listing?.booking_mode === "appointment";
  type ApptSlot = { start: string; resource_ids: string[]; resource_names: string[] };
  const [apptResources, setApptResources] = useState<{ resource_id: string; name: string }[]>([]);
  const [apptSlots, setApptSlots] = useState<ApptSlot[]>([]);
  const [apptLoadingSlots, setApptLoadingSlots] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<string>("any");
  const [apptDuration, setApptDuration] = useState<number>(0);

  const handleBack = useCallback(() => {
    if (window.history.state && typeof window.history.state.idx === 'number' && window.history.state.idx > 0) {
      navigate(-1);
    } else if (businessSlug) {
      navigate(`/${businessSlug}`);
    } else if (businessId) {
      navigate(`/book/${businessId}`);
    } else {
      navigate("/");
    }
  }, [navigate, businessSlug, businessId]);

  // Booking counts per time slot for capacity enforcement
  const [slotBookedPax, setSlotBookedPax] = useState<Record<string, number>>({});

  const { stripeConnectId, loading: paymentConfigLoading } = useBusinessPaymentConfig(businessId);

  // Detect Stripe cancel redirect
  useEffect(() => {
    if (searchParams.get("cancelled") === "true") {
      toast.error("Stripe payment was not completed. Please try again, try another card, or choose another payment method.");
      searchParams.delete("cancelled");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch listing with schedule
  useEffect(() => {
    const fetchListing = async () => {
      if (!id) return;

      const { data, error } = await supabase
        .from("listings")
        .select("*, businesses(business_id, slug, avatar_url, subscription_tier, business_name)")
        .eq("listing_id", id)
        .maybeSingle();
      if (!error && data) {
        // Parse schedule from JSONB
        const schedule = data.schedule && typeof data.schedule === "object"
          ? (data.schedule as unknown as ScheduleData)
          : null;
        setListing({ ...data, schedule } as unknown as Listing);
        const biz = (data as any).businesses;
        if (biz) {
          setBusinessSlug(biz.slug);
          setBusinessId(biz.business_id);
          setBusinessAvatarUrl(biz.avatar_url || null);
          setSubscriptionTier(biz.subscription_tier || null);
          setBusinessName(biz.business_name || null);
        }
      }
      setLoading(false);
    };

    fetchListing();
  }, [id]);

  // Parse schedule data. Normalize the shape so downstream code can rely on
  // `weekly` and `exceptions` always being present. Appointment-mode listings
  // (and minimally-seeded ones) may store just `{ weekly: {} }`.
  const schedule = useMemo<ScheduleData | null>(() => {
    if (!listing?.schedule) return null;
    const s = listing.schedule;
    if (s.weekly && typeof s.weekly === "object") {
      return { ...s, weekly: s.weekly, exceptions: Array.isArray(s.exceptions) ? s.exceptions : [] };
    }
    return null;
  }, [listing?.schedule]);

  // Get closed/exception dates for calendar blocking
  const closedDates = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    if (!schedule) return set;
    for (const exc of schedule.exceptions) {
      if (exc.isClosed) set.add(exc.date);
    }
    return set;
  }, [schedule]);

  // Check if a date has no time slots configured (no weekly slots for that day and no exception override)
  const isDateDisabled = useCallback((date: Date): boolean => {
    // Past dates
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;

    // Appointment mode: any future date is selectable. Whether staff are free
    // is resolved per-date by the availability engine (an empty slot list shows
    // a "no openings" message rather than greying out the calendar).
    if (isAppointment) return false;

    const dateStr = format(date, "yyyy-MM-dd");

    // Closed exception dates
    if (closedDates.has(dateStr)) return true;

    if (!schedule) return false;

    // Check if there's an exception with custom slots for this date
    const exception = schedule.exceptions.find(e => e.date === dateStr);
    
    if (schedule.type === 'one_off') {
      return !exception || exception.slots.length === 0;
    }

    if (exception) {
      // If exception has slots, the date is available (not disabled)
      return exception.slots.length === 0;
    }

    // Check weekly schedule for this day
    const dayName = DAY_NAMES[getDay(date)];
    const daySlots = schedule.weekly[dayName];
    if (!daySlots || daySlots.length === 0) return true;

    return false;
  }, [schedule, closedDates, isAppointment]);

  // Appointment mode: fetch resource availability for the selected date (and
  // chosen staff filter) from the shared engine edge fn.
  useEffect(() => {
    if (!isAppointment || !listing || !selectedDate) {
      setApptSlots([]);
      return;
    }
    let cancelled = false;
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    setApptLoadingSlots(true);
    supabase.functions
      .invoke("get-appointment-availability", {
        body: {
          listing_id: listing.listing_id,
          date_from: dateStr,
          date_to: dateStr,
          resource_id: selectedResourceId === "any" ? null : selectedResourceId,
        },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          setApptSlots([]);
          setApptResources([]);
        } else {
          setApptResources(data.resources ?? []);
          setApptDuration(data.duration_minutes ?? 0);
          setApptSlots(((data.days?.[0]?.slots ?? []) as ApptSlot[]).filter(
            (slot) => !isPastBookingCutoff(dateStr, slot.start, listing.booking_cutoff_hours),
          ));
        }
      })
      .finally(() => {
        if (!cancelled) setApptLoadingSlots(false);
      });
    return () => { cancelled = true; };
  }, [isAppointment, listing, selectedDate, selectedResourceId]);

  // Appointment mode invariants: single pax, pay-at-venue, no recurring.
  useEffect(() => {
    if (isAppointment) {
      setPax(1);
      setPaymentMethod("manual");
      setRecurringEnabled(false);
    }
  }, [isAppointment]);

  // Fetch booking counts for the selected date+listing to enforce capacity
  useEffect(() => {
    const fetchBookingCounts = async () => {
      if (!listing || !selectedDate) {
        setSlotBookedPax({});
        return;
      }

      const dateStr = format(selectedDate, "yyyy-MM-dd");
      try {
        const data = await getPublicSlotUsage(listing.listing_id, [dateStr]);
        const counts: Record<string, number> = {};
        for (const booking of data) {
          const time = booking.booking_time;
          counts[time] = (counts[time] || 0) + (booking.number_of_pax || 1);
        }
        setSlotBookedPax(counts);
      } catch (error) {
        console.error("Failed to fetch booking counts:", error);
        setSlotBookedPax({});
      }
    };

    fetchBookingCounts();
  }, [listing, selectedDate]);

  // Build time slots dynamically from schedule + capacity
  const timeSlots = useMemo(() => {
    if (!selectedDate || !schedule) return [];

    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const dayName = DAY_NAMES[getDay(selectedDate)];

    // Check for date exception override
    const exception = schedule.exceptions.find(e => e.date === dateStr);

    let rawSlots: string[];
    
    if (schedule.type === 'one_off') {
      if (!exception || exception.isClosed || exception.slots.length === 0) return [];
      rawSlots = exception.slots;
    } else {
      if (exception) {
        if (exception.isClosed) return [];
        rawSlots = exception.slots;
      } else {
        rawSlots = schedule.weekly[dayName] || [];
      }
    }

    if (rawSlots.length === 0) return [];

    const now = new Date();
    const isToday = selectedDate.toDateString() === now.toDateString();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const maxSpots = listing?.spots ?? 0;
    const cutoffHours = listing?.booking_cutoff_hours ?? null;

    return rawSlots
      .slice()
      .sort()
      .map(time24 => {
        const time12 = formatTimeTo12h(time24);
        const slotMinutes = parseTime24ToMinutes(time24);
        const isPast = isToday && slotMinutes <= currentMinutes;
        // Inside the owner's online-booking cut-off.
        const isClosed = !isPast && isPastBookingCutoff(dateStr, time24, cutoffHours);

        // Capacity: remaining spots for this slot
        const bookedPax = slotBookedPax[time12] || slotBookedPax[time24] || 0;
        const remainingSpots = Math.max(0, maxSpots - bookedPax);

        return {
          time: time12,
          spots: remainingSpots,
          isPast,
          isClosed,
        };
      });
  }, [selectedDate, schedule, listing?.spots, listing?.booking_cutoff_hours, slotBookedPax]);

  const cutoffNote = listing?.booking_cutoff_hours
    ? `Online booking closes ${listing.booking_cutoff_hours} hour${
      listing.booking_cutoff_hours === 1 ? "" : "s"
    } before each session.`
    : null;

  const { slotPricing } = useDynamicPricing(listing?.listing_id, listing?.price ?? 0, timeSlots, selectedDate);

  // Dynamic favicon & title for Growth+ businesses
  useDynamicFavicon(businessAvatarUrl, businessName, subscriptionTier);

  // Waitlist: surfaced when the chosen date/slot is fully booked. Session mode →
  // every generated slot is full (or the selected one is); appointment mode →
  // no available start times for the date. Opt-out via listing.waitlist_enabled.
  const waitlistEligible = useMemo(() => {
    if (!selectedDate || listing?.waitlist_enabled === false) return false;
    if (isAppointment) {
      return !apptLoadingSlots && apptSlots.length === 0;
    }
    if (selectedTime) {
      const slot = timeSlots.find((s) => s.time === selectedTime);
      if (slot && slot.spots <= 0) return true;
    }
    return timeSlots.length > 0 && timeSlots.every((s) => s.spots <= 0);
  }, [selectedDate, listing?.waitlist_enabled, isAppointment, apptLoadingSlots, apptSlots.length, selectedTime, timeSlots]);

  // Prefill from a waitlist claim redirect (?date=&time=&resource=&wl=). The
  // waitlist-claim fn sends Stripe-required claims here so the customer completes
  // the normal hold-until-paid checkout for the exact freed slot.
  const wlPrefillDone = useRef(false);
  useEffect(() => {
    if (wlPrefillDone.current || !listing) return;
    const d = searchParams.get("date");
    const t = searchParams.get("time");
    if (!d && !t) return;
    wlPrefillDone.current = true;
    if (d) {
      const parsed = new Date(`${d}T00:00:00`);
      if (!isNaN(parsed.getTime())) setSelectedDate(parsed);
    }
    if (t) setSelectedTime(t);
    const r = searchParams.get("resource");
    if (r) setSelectedResourceId(r);
  }, [listing, searchParams]);

  const selectedSlotPricing = slotPricing.find((s) => s.time === selectedTime);
  const effectivePrice = selectedSlotPricing?.adjustedPrice ?? listing?.price ?? 0;
  const originalPrice = selectedSlotPricing?.originalPrice ?? listing?.price ?? 0;
  const hasPriceAdjustment = selectedSlotPricing?.ruleName && effectivePrice !== originalPrice;
  const perBookingPrice = effectivePrice * pax;
  // Total amount displayed = sum over all booked occurrences. For a one-off
  // booking that's just one slot's price; for a recurring series it's the
  // price × N (after conflict resolution).
  const occurrencesToBook = recurringEnabled ? recurringPlan.length : 1;
  const totalPrice = perBookingPrice * (occurrencesToBook || 1);
  // For Stripe-online recurring: week 1 is the only thing actually charged
  // at checkout. Weeks 2..N are charged off-session at T−24h.
  const firstChargeAmount = perBookingPrice;

  // Max pax the user can select for the chosen time slot
  const maxPaxForSlot = useMemo(() => {
    if (!selectedTime) return listing?.spots ?? 1;
    const slot = timeSlots.find(s => s.time === selectedTime);
    return slot ? slot.spots : listing?.spots ?? 1;
  }, [selectedTime, timeSlots, listing?.spots]);

  // Reset pax if it exceeds the new max
  useEffect(() => {
    if (pax > maxPaxForSlot) {
      setPax(Math.max(1, maxPaxForSlot));
    }
  }, [maxPaxForSlot, pax]);

  // Force "pay at venue" when the booking total is $0. There's nothing to
  // charge via Stripe, so the online option must not be selectable even if
  // the business has Stripe Connect enabled.
  useEffect(() => {
    if (totalPrice <= 0 && paymentMethod !== "manual") {
      setPaymentMethod("manual");
    }
  }, [totalPrice, paymentMethod]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setSelectedTime(undefined);
    setPax(1);
  };

  // Upsert customer into the customers table and return customer_id
  const upsertCustomer = async (): Promise<string | null> => {
    if (!businessId) return null;

    try {
      return await upsertPublicCustomer({
        businessId,
        email: formData.customer_email,
        name: formData.customer_name,
        phone: formData.customer_phone || null,
      });
    } catch (error) {
      console.error("Failed to upsert customer:", error);
      return null;
    }
  };

  // Validate email format: must be user@domain.tld
  const validateEmail = (email: string): string | null => {
    if (!email.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email (e.g. you@gmail.com)";
    return null;
  };

  const handleJoinWaitlist = async () => {
    if (!listing || !selectedDate || !businessId) return;
    if (!formData.customer_name.trim()) {
      toast.error("Please enter your name so we know who to notify.");
      return;
    }
    if (!formData.customer_email.trim() && !formData.customer_phone.trim()) {
      toast.error("Add an email or phone number so we can reach you when a spot opens.");
      return;
    }
    const emailErr = formData.customer_email ? validateEmail(formData.customer_email) : null;
    if (emailErr) {
      setEmailError(emailErr);
      toast.error(emailErr);
      return;
    }
    setWaitlistJoining(true);
    try {
      const { data, error } = await supabase.functions.invoke("waitlist-join", {
        body: {
          business_id: businessId,
          listing_id: listing.listing_id,
          outlet_id: listing.outlet_id ?? null,
          desired_date: format(selectedDate, "yyyy-MM-dd"),
          // Appointment "any time" leaves desired_time null; session/appointment
          // with a chosen slot pins the exact time.
          desired_time: selectedTime ?? null,
          resource_id: isAppointment && selectedResourceId !== "any" ? selectedResourceId : null,
          party_size: pax,
          customer_name: formData.customer_name,
          customer_email: formData.customer_email || null,
          customer_phone: formData.customer_phone || null,
          custom_field_responses: customFieldResponses,
        },
      });
      if (error) {
        toast.error("Couldn't join the waitlist. Please try again.");
        return;
      }
      setWaitlistJoined(true);
      toast.success(
        data?.deduped
          ? "You're already on the waitlist for this slot."
          : "You're on the waitlist! We'll message you the moment a spot opens.",
      );
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setWaitlistJoining(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!listing || !selectedDate) {
      toast.error("Please select a date");
      return;
    }
    if (!selectedTime) {
      toast.error("Please select a time slot");
      return;
    }

    // Validate email
    const emailErr = validateEmail(formData.customer_email);
    if (emailErr) {
      setEmailError(emailErr);
      toast.error(emailErr);
      return;
    }

    // Phase C2. Attendee required when the listing collects it.
    if (listing?.collect_attendee && !attendeeName.trim()) {
      toast.error("Please enter the student's name for this class.");
      return;
    }

    // Required custom fields (applies to both modes).
    if (listing?.custom_questions) {
      for (const q of listing.custom_questions) {
        if (q.required && (!customFieldResponses[q.id] || customFieldResponses[q.id] === "")) {
          toast.error(`Please answer the required field: ${q.question}`);
          return;
        }
      }
    }

    // Appointment mode: assign a resource + atomic per-resource booking.
    if (isAppointment) {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      // selectedTime is the 24h start ("14:00"). Resolve the resource: a chosen
      // staff member, else the earliest-free in the picked slot (resource_ids[0]).
      let resourceId: string | null = selectedResourceId !== "any" ? selectedResourceId : null;
      if (!resourceId) {
        const slot = apptSlots.find((s) => s.start === selectedTime);
        resourceId = slot?.resource_ids?.[0] ?? null;
      }
      if (!resourceId) {
        toast.error("That time was just taken. Please pick another.");
        return;
      }
      setIsSubmitting(true);
      try {
        const result = await createPublicBooking({
          mode: "appointment",
          listing_id: listing.listing_id,
          resource_id: resourceId,
          booking_date: dateStr,
          booking_time: selectedTime,
          number_of_pax: 1,
          customer_name: formData.customer_name,
          customer_email: formData.customer_email,
          customer_phone: formData.customer_phone || null,
          notes: formData.notes || null,
          custom_field_responses: customFieldResponses,
        });
        navigate(bookingAccessUrl(
          `/booking-confirmed/${result.bookingId}`,
          result.accessToken,
        ));
      } catch (error) {
        if (isBookingRuleError(error)) {
          toast.error(error.message);
        } else if ((error as Error).message.includes("no longer available")) {
          toast.error("Sorry — that time was just booked. Please pick another slot.");
          setSelectedTime(undefined);
          setApptSlots((prev) => prev.filter((s) => s.start !== selectedTime));
        } else {
          toast.error("Failed to create booking. Please try again.");
        }
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Final capacity check before submit
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    let slotUsage: Array<{ booking_time: string; number_of_pax: number | null }> = [];
    let checkError: unknown = null;
    try {
      slotUsage = await getPublicSlotUsage(listing.listing_id, [dateStr]);
    } catch (error) {
      checkError = error;
    }
    const currentBookings = (slotUsage || []).filter(
      (b: any) => b.booking_time === selectedTime
    );

    if (!checkError && currentBookings) {
      const totalBooked = currentBookings.reduce((sum: number, b: any) => sum + (b.number_of_pax || 1), 0);
      const remaining = (listing.spots ?? 0) - totalBooked;
      if (pax > remaining) {
        toast.error(`Only ${remaining} spot${remaining !== 1 ? "s" : ""} remaining for this time slot. Please reduce the number of pax.`);
        // Refresh booking counts
        setSlotBookedPax(prev => ({ ...prev, [selectedTime]: totalBooked }));
        return;
      }
    }

    if (listing?.custom_questions) {
      for (const q of listing.custom_questions) {
        if (q.required && (!customFieldResponses[q.id] || customFieldResponses[q.id] === "")) {
          toast.error(`Please answer the required field: ${q.question}`);
          return;
        }
      }
    }

    if (recurringEnabled) {
      if (recurringPlan.length === 0) {
        toast.error(
          "None of the weeks you picked are available. Try a different starting date, or turn off \"Make recurring\" to book a single session instead.",
        );
        return;
      }
      // The schema requires at least 2 occurrences for a series. Anything less
      // isn't really a "series" and a one-off booking is a clearer fit. Bail
      // here with a friendly message so we don't surface the raw RPC error.
      if (recurringPlan.length < 2) {
        toast.error(
          "Only one of the weeks you picked is available — a recurring booking needs at least 2 weeks. " +
          "Pick more weeks, change the starting date, or turn off \"Make recurring\" to book just that one week.",
        );
        return;
      }
    }

    setIsSubmitting(true);

    const seriesOccurrences = recurringEnabled ? recurringPlan : null;

    if (paymentMethod === "manual") {
      if (seriesOccurrences && seriesOccurrences.length > 1) {
        // Recurring + pay-at-venue: hand off to the create_booking_series_atomic
        // RPC. The RPC does advisory-lock + capacity re-check per occurrence,
        // then inserts the booking_series + all N booking rows in one
        // transaction. If any slot was taken between the storefront preview
        // and submit (or the customer double-submits), the whole thing rolls
        // back and we surface a clear error. No orphan series rows ever.
        const occurrencesPayload = seriesOccurrences.map((occ) => ({
          date: occ.dateISO,
          time: selectedTime,
          pax,
          charge_status: "not_required",
          charge_due_at: null,
        }));

        try {
          const result = await createPublicBooking({
            mode: "series",
            listing_id: listing.listing_id,
            booking_date: dateStr,
            booking_time: selectedTime,
            number_of_pax: pax,
            customer_name: formData.customer_name,
            customer_email: formData.customer_email,
            customer_phone: formData.customer_phone || null,
            notes: formData.notes || null,
            custom_field_responses: customFieldResponses,
            occurrences: occurrencesPayload,
            attendee_name: listing.collect_attendee ? attendeeName.trim() : null,
            attendee_level: listing.collect_attendee ? attendeeLevel.trim() || null : null,
          });
          navigate(bookingAccessUrl(
            `/booking-confirmed/${result.bookingId}`,
            result.accessToken,
          ));
        } catch (error) {
          toast.error(isBookingRuleError(error)
            ? error.message
            : (error as Error).message.includes("no longer available")
            ? "One of your selected weeks just filled up. Please refresh and pick again."
            : "Failed to create recurring series. Please try again.");
          setIsSubmitting(false);
          return;
        }
      } else {
        // Single pay-at-venue booking via SECURITY DEFINER RPC (atomic capacity
        // recheck inside an advisory lock). Bookings are no longer anon-insertable
        // directly. The RPC is the audited write path.
        try {
          const result = await createPublicBooking({
            mode: "single",
            listing_id: listing.listing_id,
            booking_date: dateStr,
            booking_time: selectedTime,
            number_of_pax: pax,
            customer_name: formData.customer_name,
            customer_email: formData.customer_email,
            customer_phone: formData.customer_phone || null,
            notes: formData.notes || null,
            custom_field_responses: customFieldResponses,
            attendee_name: listing.collect_attendee ? attendeeName.trim() : null,
            attendee_level: listing.collect_attendee ? attendeeLevel.trim() || null : null,
          });
          navigate(bookingAccessUrl(
            `/booking-confirmed/${result.bookingId}`,
            result.accessToken,
          ));
        } catch (error) {
          if (isBookingRuleError(error)) {
            toast.error(error.message);
          } else if ((error as Error).message.includes("no longer available")) {
            toast.error("Sorry — that slot just filled up. Please pick another time.");
          } else {
            toast.error("Failed to create booking. Please try again.");
          }
        }
      }
    } else {
      const customerId = await upsertCustomer();

      try {
        // Per-listing billing mode: 'upfront' charges the whole programme bundle
        // in this one Checkout (the class/term model); 'per_session' charges
        // week 1 now + drips weeks 2..N via the T−24h cron. create-payment reads
        // billingMode and computes the actual charge amount from it.
        const seriesBillingMode = listing.default_billing_mode === "upfront" ? "upfront" : "per_session";
        const seriesPayload = seriesOccurrences && seriesOccurrences.length > 1
          ? {
              totalOccurrences: seriesOccurrences.length,
              weekday: selectedDate.getDay(),
              occurrenceDates: seriesOccurrences.map((o) => o.dateISO),
              perOccurrenceAmount: perBookingPrice,
              billingMode: seriesBillingMode,
            }
          : null;

        const { data, error } = await supabase.functions.invoke("stripe-create-payment", {
          body: {
            // For a series, only week 1 is charged in Checkout. Weeks 2..N
            // are charged off-session at T−24h against the saved card.
            amount: seriesPayload ? firstChargeAmount : totalPrice,
            stripeConnectId,
            customerEmail: formData.customer_email,
            customerName: formData.customer_name,
            customerPhone: formData.customer_phone || "",
            notes: formData.notes || "",
            listingId: listing.listing_id,
            bookingDate: dateStr,
            bookingTime: selectedTime,
            pax,
            customerId: customerId || "",
            test_mode: isStripeTestMode,
            successUrl: `${window.location.origin}/booking-confirmed?account=${stripeConnectId}`,
            cancelUrl: `${window.location.origin}${window.location.pathname}?cancelled=true`,
            customFieldResponses: customFieldResponses,
            series: seriesPayload,
            attendee: listing.collect_attendee
              ? { name: attendeeName.trim(), level: attendeeLevel.trim() || null }
              : null,
          },
        });

        if (error) throw await readBookingError(error, "Payment failed. Please try again.");

        if (data?.url) {
          window.location.href = data.url;
          return;
        } else {
          throw new Error("No checkout URL returned");
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Payment failed. Please try again.";
        toast.error(errorMessage);
      }
    }

    setIsSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Listing not found</p>
        <Button
          variant="outline"
          onClick={handleBack}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to business
        </Button>
      </div>
    );
  }

  // B-106: a session listing with NO schedule has zero bookable slots on every
  // date, without this guard the customer gets a "Book" button that dead-ends
  // at "No available time slots" forever. Show a clear "not accepting online
  // bookings" state instead. (Appointment mode builds slots from staff
  // availability rather than a weekly schedule, so it's exempt.)
  if (appointmentDisabled || (!isAppointment && !schedule)) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-lg font-medium">{listing.name}</p>
        <p className="text-muted-foreground max-w-md">
          This isn't open for online booking right now. Please check back soon, or contact us directly and we'll help you arrange a spot.
        </p>
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to business
        </Button>
      </div>
    );
  }

  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />} showDialog={false}>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={handleBack}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <span className="font-medium truncate">{listing.name}</span>
          </div>
        </div>

        {/* Image Gallery */}
        <div className="max-w-6xl mx-auto px-4 py-6">
          <ImageGallery images={listing.images || []} serviceName={listing.name} />
        </div>

        {/* Content */}
        <div className="max-w-6xl mx-auto px-4 pb-12">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Left Column - Service Details */}
            <div className="lg:col-span-3 space-y-6">
              <div>
                <h1 className="text-2xl sm:text-3xl font-heading font-bold">{listing.name}</h1>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    {listing.duration}
                  </span>
                  {listing.teacher_name && (
                    <span className="flex items-center gap-1.5">
                      <GraduationCap className="w-4 h-4" />
                      Taught by {listing.teacher_name}
                    </span>
                  )}
                </div>
              </div>

              <div className="border-t pt-6">
                <h2 className="font-heading font-semibold text-lg mb-3">About this service</h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                  {listing.description || "Join us for this amazing experience designed to help you achieve your wellness goals in a supportive environment."}
                </p>
              </div>

              {listing.tags && listing.tags.length > 0 && (
                <div className="border-t pt-6">
                  <h2 className="font-heading font-semibold text-lg mb-3">Additional information</h2>
                  <ul className="space-y-2 text-muted-foreground">
                    {listing.tags.map((tag) => {
                      const info = TAG_INFO[tag];
                      if (!info) return null;
                      const Icon = info.icon;
                      return (
                        <li key={tag} className="flex items-center gap-2.5">
                          <Icon className="w-4 h-4 text-primary" />
                          <span>{info.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {listing.faqs && listing.faqs.length > 0 && (
                <div className="border-t pt-6">
                  <h2 className="font-heading font-semibold text-lg mb-3">Frequently Asked Questions</h2>
                  <Accordion type="single" collapsible className="w-full">
                    {listing.faqs.map((faq, idx) => (
                      <AccordionItem key={idx} value={`item-${idx}`}>
                        <AccordionTrigger className="text-left text-sm font-medium hover:no-underline hover:text-primary">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground leading-relaxed text-sm">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}

              <div className="border-t pt-6">
                <h2 className="font-heading font-semibold text-lg mb-3">Terms & conditions</h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line text-sm">
                  {listing.terms_and_conditions || "All bookings are subject to availability. Cancellations must be made at least 24 hours in advance for a full refund. Late arrivals may result in a shortened session. The organiser reserves the right to reschedule or cancel due to unforeseen circumstances, in which case a full refund or alternative date will be offered."}
                </p>
              </div>

              {/* Share Section */}
              <div className="border-t pt-6 pb-4">
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Share:</span>
                  <div className="flex items-center gap-2">
                    <button 
                      type="button"
                      onClick={() => {
                        const url = window.location.href;
                        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                      }}
                      className="p-2 hover:bg-muted rounded-full transition-colors"
                      title="Share to Facebook"
                    >
                      <Facebook className="w-4 h-4" />
                    </button>
                    <button 
                      type="button"
                      onClick={() => {
                        const url = window.location.href;
                        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(listing.name + ' - ' + url)}`, '_blank');
                      }}
                      className="p-2 hover:bg-muted rounded-full transition-colors"
                      title="Share to WhatsApp"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </button>
                    <button 
                      type="button"
                      onClick={() => {
                        const url = window.location.href;
                        window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(listing.name)}`, '_blank');
                      }}
                      className="p-2 hover:bg-muted rounded-full transition-colors"
                      title="Share to Telegram"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                    <button 
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        toast.success("Link copied to clipboard");
                      }}
                      className="p-2 hover:bg-muted rounded-full transition-colors"
                      title="Copy Link"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Booking Card */}
            <div className="lg:col-span-2">
              <div className="sticky top-20">
                <div className="bg-card rounded-xl p-6 shadow-card border border-border/50">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Booking Details Section */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Booking details</Label>

                      {/* Date field */}
                      <div className="border border-input rounded-xl overflow-hidden bg-card">
                        <button
                          type="button"
                          onClick={() => {
                            setCalendarOpen(!calendarOpen);
                            if (!calendarOpen) setTimeOpen(false);
                          }}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary transition-colors"
                        >
                          <div className="text-left">
                            <div className="text-[10px] font-semibold text-muted-foreground leading-none">Date</div>
                            <div className="text-sm font-medium mt-1">
                              {selectedDate ? format(selectedDate, "dd/MM/yyyy") : "Select date"}
                            </div>
                          </div>
                          <ChevronDown className={cn(
                            "w-5 h-5 text-muted-foreground transition-transform duration-200",
                            calendarOpen && "rotate-180"
                          )} />
                        </button>

                        {calendarOpen && (
                          <div className="border-t animate-in slide-in-from-top-2 duration-200">
                            <Calendar
                              mode="single"
                              selected={selectedDate}
                              onSelect={(date) => {
                                handleDateSelect(date);
                                if (date) {
                                  setCalendarOpen(false);
                                  setTimeOpen(true);
                                }
                              }}
                              disabled={isDateDisabled}
                              showOutsideDays={false}
                              modifiers={{
                                blocked: (date: Date) => closedDates.has(format(date, "yyyy-MM-dd")),
                              }}
                              modifiersClassNames={{
                                blocked: "line-through text-destructive/50 bg-destructive/5",
                              }}
                              className={cn("p-3 pointer-events-auto w-full")}
                              classNames={{
                                months: "flex flex-col space-y-4",
                                month: "space-y-4 w-full",
                                caption: "flex justify-center pt-1 relative items-center",
                                caption_label: "text-sm font-medium",
                                nav: "space-x-1 flex items-center",
                                nav_button: cn(
                                  "h-8 w-8 bg-transparent p-0 opacity-50 hover:opacity-100 inline-flex items-center justify-center rounded-full hover:bg-accent hover:text-accent-foreground transition-colors"
                                ),
                                nav_button_previous: "absolute left-1",
                                nav_button_next: "absolute right-1",
                                table: "w-full border-collapse",
                                head_row: "flex w-full",
                                head_cell: "text-muted-foreground rounded-md w-full font-normal text-[0.8rem] flex-1 text-center",
                                row: "flex w-full mt-1",
                                cell: cn(
                                  "relative flex-1 p-0.5 text-center text-sm focus-within:relative focus-within:z-20"
                                ),
                                day: cn(
                                  "h-9 w-9 mx-auto p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md inline-flex items-center justify-center transition-colors"
                                ),
                                day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                                day_today: "border-2 border-primary text-primary font-medium",
                                day_outside: "text-muted-foreground opacity-50",
                                day_disabled: "text-muted-foreground opacity-30",
                                day_hidden: "invisible",
                              }}
                            />
                          </div>
                        )}

                      </div>

                      {/* Time field */}
                      <div className="border border-input rounded-xl overflow-hidden bg-card">
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedDate) {
                              setTimeOpen(!timeOpen);
                              if (!timeOpen) setCalendarOpen(false);
                            }
                          }}
                          className={cn(
                            "w-full flex items-center justify-between px-4 py-3 transition-colors",
                            selectedDate ? "hover:bg-secondary" : "opacity-50 cursor-not-allowed"
                          )}
                          disabled={!selectedDate}
                        >
                          <div className="text-left">
                            <div className="text-[10px] font-semibold text-muted-foreground leading-none">Time</div>
                            <div className="text-sm font-medium mt-1">
                              {selectedTime ? (isAppointment ? formatTimeTo12h(selectedTime) : selectedTime) : "Select time"}
                            </div>
                          </div>
                          <ChevronDown className={cn(
                            "w-5 h-5 text-muted-foreground transition-transform duration-200",
                            timeOpen && "rotate-180"
                          )} />
                        </button>

                        {timeOpen && selectedDate && !isAppointment && (
                          <div className="border-t animate-in slide-in-from-top-2 duration-200 p-3">
                            {slotPricing.length > 0 ? (
                              <DynamicTimeSlots
                                slots={slotPricing}
                                selectedTime={selectedTime}
                                onSelectTime={(time) => {
                                  setSelectedTime(time);
                                  setTimeOpen(false);
                                  // Reset pax when time changes
                                  setPax(1);
                                }}
                              />
                            ) : (
                              <div className="text-center py-6 text-muted-foreground">
                                <p className="text-sm">No available time slots for this date</p>
                              </div>
                            )}
                            {cutoffNote && (
                              <p className="mt-2 text-center text-xs text-muted-foreground">{cutoffNote}</p>
                            )}
                          </div>
                        )}

                        {/* Appointment mode: staff picker + resource-aware start times */}
                        {timeOpen && selectedDate && isAppointment && (
                          <div className="border-t animate-in slide-in-from-top-2 duration-200 p-3 space-y-3">
                            {apptResources.length > 1 && (
                              <div className="space-y-1.5">
                                <Label className="text-xs">Staff</Label>
                                <Select
                                  value={selectedResourceId}
                                  onValueChange={(v) => { setSelectedResourceId(v); setSelectedTime(undefined); }}
                                >
                                  <SelectTrigger><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="any">Any available</SelectItem>
                                    {apptResources.map((r) => (
                                      <SelectItem key={r.resource_id} value={r.resource_id}>{r.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            {apptLoadingSlots ? (
                              <div className="text-center py-6 text-muted-foreground"><p className="text-sm">Loading times…</p></div>
                            ) : apptSlots.length > 0 ? (
                              <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                                {apptSlots.map((s) => {
                                  const label = formatTimeTo12h(s.start);
                                  const isSel = selectedTime === s.start;
                                  return (
                                    <button
                                      key={s.start}
                                      type="button"
                                      onClick={() => { setSelectedTime(s.start); setTimeOpen(false); }}
                                      className={cn(
                                        "px-2 py-2 rounded-lg border text-sm transition-colors",
                                        isSel ? "border-primary ring-2 ring-primary/30 bg-primary/5" : "border-input hover:border-muted-foreground/40"
                                      )}
                                    >
                                      {label}
                                    </button>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-center py-6 text-muted-foreground">
                                <p className="text-sm">{selectedResourceId === "any" ? "No openings on this date" : "This staff member has no openings on this date"}</p>
                              </div>
                            )}
                            {cutoffNote && (
                              <p className="text-center text-xs text-muted-foreground">{cutoffNote}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Recurring booking — session mode only */}
                    {listing && schedule && !isAppointment && (
                      <RecurringBookingPanel
                        listingId={listing.listing_id}
                        totalSpots={listing.spots ?? 0}
                        pricePerPax={effectivePrice}
                        pax={pax}
                        selectedDate={selectedDate}
                        selectedTime={selectedTime}
                        schedule={schedule}
                        enabled={recurringEnabled}
                        paymentMethod={listing.price === 0 ? "manual" : paymentMethod}
                        billingMode={listing.default_billing_mode === "upfront" ? "upfront" : "per_session"}
                        onEnabledChange={setRecurringEnabled}
                        onPlanChange={setRecurringPlan}
                      />
                    )}

                    {/* Attendee (Phase C2) — who the class is for. Shown only when
                        the owner enabled it on this listing. The booker below is
                        the paying parent/guardian; this is the child. */}
                    {listing?.collect_attendee && (
                      <div className="space-y-4 pt-4 border-t">
                        <div>
                          <Label className="text-sm font-medium">Who's attending?</Label>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            The student joining this class (if that's not you).
                          </p>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="attendee-name">
                            Student's name<span className="text-destructive ml-0.5">*</span>
                          </Label>
                          <Input
                            id="attendee-name"
                            value={attendeeName}
                            onChange={(e) => setAttendeeName(e.target.value)}
                            placeholder="e.g. Aarav Kumar"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="attendee-level">Level / grade (optional)</Label>
                          <Input
                            id="attendee-level"
                            value={attendeeLevel}
                            onChange={(e) => setAttendeeLevel(e.target.value)}
                            placeholder="e.g. P5, Sec 2, Grade 3"
                          />
                        </div>
                      </div>
                    )}

                    {/* Number of pax + Contact Details - inline stacked fields */}
                    <div className="space-y-4 pt-4 border-t">
                      <Label className="text-sm font-medium">
                        {listing?.collect_attendee ? "Parent / guardian details" : "Your details"}
                      </Label>

                      {/* Pax row — hidden for appointments (one resource = one booking) */}
                      {!isAppointment && (
                      <div className="space-y-1.5">
                        <Label>
                          Pax
                          {selectedTime && maxPaxForSlot < (listing.spots ?? 0) && (
                            <span className="text-xs text-muted-foreground ml-2 font-normal">
                              ({maxPaxForSlot} spot{maxPaxForSlot !== 1 ? "s" : ""} left)
                            </span>
                          )}
                        </Label>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setPax(Math.max(1, pax - 1))}
                            className="h-10 w-10 shrink-0 rounded-md border border-input bg-card flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-50"
                            disabled={pax <= 1}
                          >
                            <span className="text-base font-medium">−</span>
                          </button>
                          <Input
                            type="number"
                            inputMode="numeric"
                            value={pax}
                            min={1}
                            max={maxPaxForSlot}
                            onChange={(e) => {
                              const v = parseInt(e.target.value, 10);
                              if (Number.isNaN(v)) {
                                setPax(1);
                                return;
                              }
                              setPax(Math.min(maxPaxForSlot, Math.max(1, v)));
                            }}
                            className="flex-1 text-center font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          <button
                            type="button"
                            onClick={() => setPax(Math.min(maxPaxForSlot, pax + 1))}
                            className="h-10 w-10 shrink-0 rounded-md border border-input bg-card flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-50"
                            disabled={pax >= maxPaxForSlot}
                          >
                            <span className="text-base font-medium">+</span>
                          </button>
                        </div>
                      </div>
                      )}

                      {/* Name */}
                      <div className="space-y-1.5">
                        <Label htmlFor="booking-name">Name</Label>
                        <Input
                          id="booking-name"
                          value={formData.customer_name}
                          onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                          placeholder="Your name"
                          required
                        />
                      </div>

                      {/* Email */}
                      <div className="space-y-1.5">
                        <Label htmlFor="booking-email" className={cn(emailError && "text-destructive")}>Email</Label>
                        <Input
                          id="booking-email"
                          type="email"
                          value={formData.customer_email}
                          onChange={(e) => {
                            setFormData({ ...formData, customer_email: e.target.value });
                            if (emailError) setEmailError(null);
                          }}
                          onBlur={() => {
                            if (formData.customer_email.trim()) {
                              setEmailError(validateEmail(formData.customer_email));
                            }
                          }}
                          placeholder="you@example.com"
                          required
                          aria-invalid={!!emailError}
                          className={cn(emailError && "border-destructive focus-visible:ring-destructive")}
                        />
                        {emailError && (
                          <p className="text-xs text-destructive">{emailError}</p>
                        )}
                      </div>

                      {/* Phone */}
                      <div className="space-y-1.5">
                        <Label htmlFor="booking-phone">Phone (optional)</Label>
                        <div className="flex h-10 w-full items-center rounded-md border border-input bg-card px-3 ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                          <PhoneInput
                            id="booking-phone"
                            international
                            defaultCountry="SG"
                            placeholder="+65 8123 4567"
                            value={formData.customer_phone || ""}
                            onChange={(val) => setFormData({ ...formData, customer_phone: val || "" })}
                            className="phone-input-onboarding"
                          />
                        </div>
                      </div>

                      {/* Custom Fields */}
                      {listing?.custom_questions && listing.custom_questions.map((q) => (
                        <div key={q.id} className="space-y-1.5">
                          <Label htmlFor={`booking-q-${q.id}`}>
                            {q.question}
                            {q.required && <span className="text-destructive ml-0.5">*</span>}
                          </Label>

                          {q.type === "text" && (
                            <Input
                              id={`booking-q-${q.id}`}
                              type="text"
                              value={customFieldResponses[q.id] || ""}
                              onChange={(e) => setCustomFieldResponses({ ...customFieldResponses, [q.id]: e.target.value })}
                              placeholder="Your answer"
                              required={q.required}
                            />
                          )}

                          {q.type === "dropdown" && (
                            <Select
                              value={customFieldResponses[q.id] || ""}
                              onValueChange={(value) => setCustomFieldResponses({ ...customFieldResponses, [q.id]: value })}
                            >
                              <SelectTrigger id={`booking-q-${q.id}`}>
                                <SelectValue placeholder="Select an option" />
                              </SelectTrigger>
                              <SelectContent>
                                {q.options?.map((opt, i) => (
                                  <SelectItem key={i} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}

                          {q.type === "radio" && (
                            <div className="flex flex-col gap-2 pt-1">
                              {(q.options || []).map((opt, i) => (
                                <label key={i} className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`custom-radio-${q.id}`}
                                    value={opt}
                                    checked={customFieldResponses[q.id] === opt}
                                    onChange={() => setCustomFieldResponses({ ...customFieldResponses, [q.id]: opt })}
                                    required={q.required && !customFieldResponses[q.id]}
                                    className="h-4 w-4 border-input text-primary focus:ring-primary"
                                  />
                                  <span className="text-sm font-medium">{opt}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Notes */}
                      <div className="space-y-1.5">
                        <Label htmlFor="booking-notes">Notes (optional)</Label>
                        <Textarea
                          id="booking-notes"
                          placeholder="Anything we should take note of?"
                          value={formData.notes || ""}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          rows={3}
                        />
                      </div>
                    </div>

                    {/* Payment Method */}
                    {!paymentConfigLoading && listing?.price !== 0 && (
                      <div className="space-y-3 pt-2 border-t">
                        <Label className="text-sm font-medium">Payment</Label>
                        <PaymentMethodSelector
                          value={paymentMethod}
                          onChange={setPaymentMethod}
                          stripeAvailable={!!stripeConnectId && totalPrice > 0}
                        />
                      </div>
                    )}

                    {/* Price and Submit */}
                    <div className="pt-4 border-t space-y-4">
                      <div className="flex items-baseline justify-between">
                        <div>
                          {hasPriceAdjustment ? (
                            <>
                              <span className="text-lg text-muted-foreground line-through mr-2">
                                ${(originalPrice * pax).toFixed(2)}
                              </span>
                              <span className="text-2xl font-bold text-green-600">
                                ${totalPrice.toFixed(2)}
                              </span>
                              {pax > 1 && (
                                <span className="text-sm text-muted-foreground ml-2">
                                  (${effectivePrice.toFixed(2)}/pax for {pax} pax)
                                </span>
                              )}
                              <div className="text-xs mt-1 text-green-600">
                                {selectedSlotPricing?.ruleName}
                                {selectedSlotPricing?.discountPercent
                                  ? ` — ${selectedSlotPricing.discountPercent}% off`
                                  : ""}
                              </div>
                            </>
                          ) : (
                            <>
                              <span className="text-2xl font-bold">${totalPrice.toFixed(2)}</span>
                              {pax > 1 && (
                                <span className="text-sm text-muted-foreground ml-2">
                                  (${effectivePrice.toFixed(2)}/pax for {pax} pax)
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {waitlistEligible ? (
                        waitlistJoined ? (
                          <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
                            <p className="text-sm font-semibold text-green-800">You're on the waitlist 🎉</p>
                            <p className="text-xs text-green-700 mt-1">
                              We'll message you the moment a spot opens — you'll have a short window to grab it.
                            </p>
                          </div>
                        ) : (
                          <div className="rounded-xl border bg-muted/40 p-4 space-y-3">
                            <div>
                              <p className="text-sm font-semibold">This {isAppointment ? "day" : "slot"} is fully booked</p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Join the waitlist and we'll notify you the instant someone cancels.
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="secondary"
                              className="w-full h-12 text-base font-semibold"
                              disabled={waitlistJoining}
                              onClick={handleJoinWaitlist}
                            >
                              {waitlistJoining ? (
                                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Joining…</>
                              ) : (
                                "Join the waitlist"
                              )}
                            </Button>
                          </div>
                        )
                      ) : (
                        <Button
                          type="submit"
                          className="w-full h-12 text-base font-semibold"
                          disabled={isSubmitting || !selectedDate || !selectedTime}
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              {paymentMethod === "stripe" ? "Processing payment..." : "Booking..."}
                            </>
                          ) : (
                            paymentMethod === "stripe" ? "Pay & book now" : "Book now"
                          )}
                        </Button>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Sentry.ErrorBoundary>
  );
};

export default ListingBooking;
